/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

export interface ICustomRangeSliderProps {
    id?: string;
    className?: string;
    min: number;
    max: number;
    step?: number;
    minValue: number;
    maxValue: number;
    minLabel?: string;
    maxLabel?: string;
    disabled?: boolean;
    formatTooltip?: (value: number) => string;
    onChange?: (minValue: number, maxValue: number) => void;
    onChangeEnd?: (minValue: number, maxValue: number) => void;
}

interface ICustomRangeSliderState {
    isDraggingMin: boolean;
    isDraggingMax: boolean;
    localMinValue: number;
    localMaxValue: number;
}

/**
 * Custom dual-thumb range slider component.
 */
export default class CustomRangeSlider extends React.Component<ICustomRangeSliderProps, ICustomRangeSliderState> {
    private sliderRef: React.RefObject<HTMLDivElement>;
    private minThumbRef: React.RefObject<HTMLDivElement>;
    private maxThumbRef: React.RefObject<HTMLDivElement>;

    public constructor(props: ICustomRangeSliderProps) {
        super(props);
        this.sliderRef = React.createRef<HTMLDivElement>();
        this.minThumbRef = React.createRef<HTMLDivElement>();
        this.maxThumbRef = React.createRef<HTMLDivElement>();

        this.state = {
            isDraggingMin: false,
            isDraggingMax: false,
            localMinValue: props.minValue,
            localMaxValue: props.maxValue
        };

        this._handleMinThumbMouseDown = this._handleMinThumbMouseDown.bind(this);
        this._handleMaxThumbMouseDown = this._handleMaxThumbMouseDown.bind(this);
        this._handleMouseMove = this._handleMouseMove.bind(this);
        this._handleMouseUp = this._handleMouseUp.bind(this);
        this._handleTrackClick = this._handleTrackClick.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
    }

    public componentDidUpdate(prevProps: ICustomRangeSliderProps): void {
        // Update local state when props change (e.g., from input fields)
        if (prevProps.minValue !== this.props.minValue || prevProps.maxValue !== this.props.maxValue) {
            if (!this.state.isDraggingMin && !this.state.isDraggingMax) {
                this.setState({
                    localMinValue: this.props.minValue,
                    localMaxValue: this.props.maxValue
                });
            }
        }
    }

    public componentWillUnmount(): void {
        document.removeEventListener('mousemove', this._handleMouseMove);
        document.removeEventListener('mouseup', this._handleMouseUp);
        document.removeEventListener('touchmove', this._handleMouseMove);
        document.removeEventListener('touchend', this._handleMouseUp);
    }

    public render(): JSX.Element {
        const { id, className, min, max, minLabel, maxLabel, disabled, formatTooltip } = this.props;
        const { localMinValue, localMaxValue } = this.state;

        const range = max - min;
        const minPercent = ((localMinValue - min) / range) * 100;
        const maxPercent = ((localMaxValue - min) / range) * 100;

        const formatValue = formatTooltip || ((value: number) => value.toString());

        return (
            <div
                id={id}
                className={`custom-range-slider ${className || ''} ${disabled ? 'custom-range-slider--disabled' : ''}`}
                ref={this.sliderRef}
            >
                {/* Track */}
                <div className='custom-range-slider__track' onClick={this._handleTrackClick}>
                    {/* Active range */}
                    <div
                        className='custom-range-slider__range'
                        style={{
                            left: `${minPercent}%`,
                            width: `${maxPercent - minPercent}%`
                        }}
                    />

                    {/* Min thumb */}
                    <div
                        ref={this.minThumbRef}
                        className='custom-range-slider__thumb custom-range-slider__thumb--min'
                        style={{ left: `${minPercent}%` }}
                        onMouseDown={this._handleMinThumbMouseDown}
                        onTouchStart={this._handleMinThumbMouseDown}
                        onKeyDown={e => this._handleKeyDown(e, 'min')}
                        role='slider'
                        tabIndex={disabled ? -1 : 0}
                        aria-valuemin={min}
                        aria-valuemax={localMaxValue}
                        aria-valuenow={localMinValue}
                        aria-valuetext={formatValue(localMinValue)}
                        aria-label='Minimum value'
                        aria-disabled={disabled}
                    />

                    {/* Max thumb */}
                    <div
                        ref={this.maxThumbRef}
                        className='custom-range-slider__thumb custom-range-slider__thumb--max'
                        style={{ left: `${maxPercent}%` }}
                        onMouseDown={this._handleMaxThumbMouseDown}
                        onTouchStart={this._handleMaxThumbMouseDown}
                        onKeyDown={e => this._handleKeyDown(e, 'max')}
                        role='slider'
                        tabIndex={disabled ? -1 : 0}
                        aria-valuemin={localMinValue}
                        aria-valuemax={max}
                        aria-valuenow={localMaxValue}
                        aria-valuetext={formatValue(localMaxValue)}
                        aria-label='Maximum value'
                        aria-disabled={disabled}
                    />
                </div>

                {/* Labels */}
                <div className='custom-range-slider__labels'>
                    <span className='custom-range-slider__label custom-range-slider__label--min'>{minLabel || formatValue(min)}</span>
                    <span className='custom-range-slider__label custom-range-slider__label--max'>{maxLabel || formatValue(max)}</span>
                </div>
            </div>
        );
    }

    private _handleMinThumbMouseDown(e: React.MouseEvent | React.TouchEvent): void {
        if (this.props.disabled) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDraggingMin: true });
        document.addEventListener('mousemove', this._handleMouseMove);
        document.addEventListener('mouseup', this._handleMouseUp);
        document.addEventListener('touchmove', this._handleMouseMove);
        document.addEventListener('touchend', this._handleMouseUp);
    }

    private _handleMaxThumbMouseDown(e: React.MouseEvent | React.TouchEvent): void {
        if (this.props.disabled) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDraggingMax: true });
        document.addEventListener('mousemove', this._handleMouseMove);
        document.addEventListener('mouseup', this._handleMouseUp);
        document.addEventListener('touchmove', this._handleMouseMove);
        document.addEventListener('touchend', this._handleMouseUp);
    }

    private _handleMouseMove(e: MouseEvent | TouchEvent): void {
        const { min, max, step = 1, onChange } = this.props;
        const { isDraggingMin, isDraggingMax, localMinValue, localMaxValue } = this.state;

        if (!isDraggingMin && !isDraggingMax) {
            return;
        }

        const slider = this.sliderRef.current;
        if (!slider) {
            return;
        }

        const rect = slider.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const rawValue = min + percent * (max - min);
        const steppedValue = Math.round(rawValue / step) * step;
        const clampedValue = Math.max(min, Math.min(max, steppedValue));

        if (isDraggingMin) {
            const newMinValue = Math.min(clampedValue, localMaxValue - step);
            if (newMinValue !== localMinValue) {
                this.setState({ localMinValue: newMinValue });
                if (onChange) {
                    onChange(newMinValue, localMaxValue);
                }
            }
        } else if (isDraggingMax) {
            const newMaxValue = Math.max(clampedValue, localMinValue + step);
            if (newMaxValue !== localMaxValue) {
                this.setState({ localMaxValue: newMaxValue });
                if (onChange) {
                    onChange(localMinValue, newMaxValue);
                }
            }
        }
    }

    private _handleMouseUp(): void {
        const { onChangeEnd } = this.props;
        const { isDraggingMin, isDraggingMax, localMinValue, localMaxValue } = this.state;

        if (isDraggingMin || isDraggingMax) {
            this.setState({ isDraggingMin: false, isDraggingMax: false });
            if (onChangeEnd) {
                onChangeEnd(localMinValue, localMaxValue);
            }
        }

        document.removeEventListener('mousemove', this._handleMouseMove);
        document.removeEventListener('mouseup', this._handleMouseUp);
        document.removeEventListener('touchmove', this._handleMouseMove);
        document.removeEventListener('touchend', this._handleMouseUp);
    }

    private _handleTrackClick(e: React.MouseEvent): void {
        if (this.props.disabled) {
            return;
        }

        const { min, max, step = 1, onChange, onChangeEnd } = this.props;
        const { localMinValue, localMaxValue } = this.state;

        const slider = this.sliderRef.current;
        if (!slider) {
            return;
        }

        const rect = slider.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const rawValue = min + percent * (max - min);
        const steppedValue = Math.round(rawValue / step) * step;
        const clampedValue = Math.max(min, Math.min(max, steppedValue));

        // Determine which thumb is closer
        const distToMin = Math.abs(clampedValue - localMinValue);
        const distToMax = Math.abs(clampedValue - localMaxValue);

        if (distToMin <= distToMax && clampedValue < localMaxValue) {
            this.setState({ localMinValue: clampedValue }, () => {
                if (onChange) {
                    onChange(clampedValue, localMaxValue);
                }
                if (onChangeEnd) {
                    onChangeEnd(clampedValue, localMaxValue);
                }
            });
        } else if (clampedValue > localMinValue) {
            this.setState({ localMaxValue: clampedValue }, () => {
                if (onChange) {
                    onChange(localMinValue, clampedValue);
                }
                if (onChangeEnd) {
                    onChangeEnd(localMinValue, clampedValue);
                }
            });
        }
    }

    private _handleKeyDown(e: React.KeyboardEvent, thumb: 'min' | 'max'): void {
        if (this.props.disabled) {
            return;
        }

        const { min, max, step = 1, onChange, onChangeEnd } = this.props;
        const { localMinValue, localMaxValue } = this.state;

        let delta = 0;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            delta = -step;
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            delta = step;
        } else if (e.key === 'Home') {
            delta = thumb === 'min' ? min - localMinValue : localMinValue - localMaxValue + step;
        } else if (e.key === 'End') {
            delta = thumb === 'min' ? localMaxValue - localMinValue - step : max - localMaxValue;
        } else {
            return;
        }

        e.preventDefault();

        if (thumb === 'min') {
            const newValue = Math.max(min, Math.min(localMaxValue - step, localMinValue + delta));
            if (newValue !== localMinValue) {
                this.setState({ localMinValue: newValue }, () => {
                    if (onChange) {
                        onChange(newValue, localMaxValue);
                    }
                    if (onChangeEnd) {
                        onChangeEnd(newValue, localMaxValue);
                    }
                });
            }
        } else {
            const newValue = Math.max(localMinValue + step, Math.min(max, localMaxValue + delta));
            if (newValue !== localMaxValue) {
                this.setState({ localMaxValue: newValue }, () => {
                    if (onChange) {
                        onChange(localMinValue, newValue);
                    }
                    if (onChangeEnd) {
                        onChangeEnd(localMinValue, newValue);
                    }
                });
            }
        }
    }
}
